import { useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Sparkles, Heart, Flag, X, Check, Camera, Star, MessageCircle, Zap, Clock, ArrowUpDown, Baby, BookOpen, Globe, Briefcase, GlassWater, Ban, Award, Users, Ring, Gem, UserPlus, UserX, ImageOff, ShieldOff, EyeOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { toast } from "sonner";
import PhotoExpandModal from "../components/PhotoExpandModal";
import PremiumBadge from "../components/PremiumBadge";
import MatchModal from "../components/MatchModal";
import { useAuth } from "../lib/auth";
import { tgHapticImpact, tgHapticNotification } from "../lib/telegram";

const REPORT_REASONS = [
  { id: "fake_profile", label: "Фейковый профиль", icon: UserX },
  { id: "fake_photos", label: "Ненастоящие фото", icon: ImageOff },
  { id: "harassment", label: "Оскорбления / домогательства", icon: ShieldOff },
  { id: "spam", label: "Спам / реклама", icon: MessageCircle },
  { id: "underage", label: "Несовершеннолетний(ая)", icon: Ban },
  { id: "offensive_content", label: "Неприемлемый контент", icon: EyeOff },
  { id: "commercial", label: "Коммерческая деятельность", icon: Briefcase },
  { id: "other", label: "Другое", icon: Flag },
];

export default function PublicProfile() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const profile = location.state?.profile || null;
  const backTo = location.state?.backTo || "/discover";
  const matched = location.state?.matched || false;
  const [photoIdx, setPhotoIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportComment, setReportComment] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [liking, setLiking] = useState(false);
  const [matchModal, setMatchModal] = useState(null);
  const [lastMatchId, setLastMatchId] = useState(null);

  const photos = profile?.photos || [];
  const current = photos[photoIdx];
  const SWIPE_THRESHOLD = 50;

  const handlePrev = useCallback(() => {
    setPhotoIdx((i) => (i > 0 ? i - 1 : (photos.length || 1) - 1));
  }, [photos.length]);

  const handleNext = useCallback(() => {
    setPhotoIdx((i) => (i < (photos.length || 1) - 1 ? i + 1 : 0));
  }, [photos.length]);

  const handleSwipe = useCallback((_, info) => {
    if (info.offset.x < -SWIPE_THRESHOLD) handleNext();
    if (info.offset.x > SWIPE_THRESHOLD) handlePrev();
  }, [handleNext, handlePrev]);

  const sendLike = async (type) => {
    if (liking) return;
    setLiking(true);
    try {
      const { data } = await api.post("/likes", { to_user_id: profile.id, type });
      if (data.matched) {
        tgHapticNotification("success");
        setMatchModal(data.matched_user);
        setLastMatchId(data.match?.id);
      } else {
        tgHapticImpact("light");
        nav(backTo);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось отправить лайк");
    } finally {
      setLiking(false);
    }
  };

  const openChat = () => {
    if (lastMatchId) {
      setMatchModal(null);
      nav(`/chat/${lastMatchId}`);
    }
  };

  const submitReport = async () => {
    if (!reportReason) {
      toast.error("Выберите причину жалобы");
      return;
    }
    setReportSending(true);
    try {
      await api.post(`/report/${profile.id}`, {
        reason: reportReason,
        comment: reportComment.trim(),
      });
      toast.success("Жалоба отправлена администратору");
      setReportOpen(false);
      setReportReason("");
      setReportComment("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось отправить жалобу");
    } finally {
      setReportSending(false);
    }
  };

  if (!profile) {
    return (
      <div className="kupidon-shell safe-top safe-bottom flex flex-col items-center justify-center px-6 text-center">
        <div className="font-[Onest] font-bold text-2xl">Профиль не найден</div>
        <button
          onClick={() => nav(backTo, { replace: true })}
          className="btn-pill mt-5 bg-[color:var(--ink)] text-white px-6"
        >
          Назад
        </button>
      </div>
    );
  }

  const details = [
    profile.height_cm ? { id: "height", label: "Рост", value: `${profile.height_cm} см`, icon: ArrowUpDown } : null,
    profile.zodiac_sign ? { id: "zodiac", label: "Знак зодиака", value: profile.zodiac_sign, icon: Star } : null,
    profile.worldview ? { id: "worldview", label: "Мировоззрение", value: profile.worldview, icon: Globe } : null,
    profile.education ? { id: "education", label: "Образование", value: profile.education, icon: Award } : null,
    profile.children ? { id: "children", label: "Дети", value: profile.children, icon: Baby } : null,
    (profile.spoken_languages || []).length > 0
      ? { id: "langs", label: "Языки", value: profile.spoken_languages.join(", "), icon: MessageCircle }
      : null,
    profile.alcohol ? { id: "alcohol", label: "Алкоголь", value: profile.alcohol, icon: GlassWater } : null,
    profile.smoking ? { id: "smoking", label: "Курение", value: profile.smoking, icon: Ban } : null,
    profile.work_field ? { id: "work", label: "Сфера", value: profile.work_field, icon: Briefcase } : null,
  ].filter(Boolean);

  // Seeking display
  const seekingLabels = {
    love: "Любовь",
    relationship: "Отношения",
    dates: "Свидания",
    friendship: "Дружба",
    penpal: "Переписка",
    networking: "Общение",
    marriage: "Семья",
  };
  const seekingIcons = {
    love: Heart,
    relationship: Gem,
    dates: Star,
    friendship: Users,
    penpal: MessageCircle,
    networking: Globe,
    marriage: Sparkles,
  };
  const seekingList = (profile.seeking || []).map((s) => ({ label: seekingLabels[s] || s, icon: seekingIcons[s] || Heart })).filter((s) => s.label);

  // Activity status
  const lastActive = profile.last_active ? new Date(profile.last_active) : null;
  const now = new Date();
  let activityText = "";
  let activityColor = "";
  if (lastActive) {
    const diffMin = Math.floor((now - lastActive) / 60000);
    if (diffMin < 5) { activityText = "В сети"; activityColor = "text-green-500"; }
    else if (diffMin < 60) { activityText = `${diffMin} мин назад`; activityColor = "text-[color:var(--ink-soft)]"; }
    else if (diffMin < 1440) { activityText = `${Math.floor(diffMin / 60)} ч назад`; activityColor = "text-[color:var(--ink-soft)]"; }
    else { activityText = `${Math.floor(diffMin / 1440)} дн назад`; activityColor = "text-[color:var(--ink-soft)]"; }
  }

  // Compatibility score — computed server-side (compute_compatibility() in
  // server.py) by comparing the viewer's own profile against this one: mutual
  // gender/looking_for, seeking overlap, age, languages, worldview, children,
  // alcohol/smoking. Missing data on either side is excluded, not penalized.
  const compatScore = profile.compatibility ?? 0;
  const compatLabel = compatScore >= 80 ? "Высокая" : compatScore >= 60 ? "Хорошая" : compatScore >= 45 ? "Средняя" : "Низкая";
  const compatColor = compatScore >= 80 ? "from-green-400 to-emerald-500" : compatScore >= 60 ? "from-[color:var(--peach)] to-[color:var(--peach-deep)]" : "from-yellow-400 to-orange-400";

  return (
    <div className="kupidon-shell safe-top safe-bottom flex flex-col relative overflow-y-auto no-scrollbar bg-gradient-to-b from-[#FFF5F0] via-white to-[#FFF5F0]">
      {/* Profile header — back + report; buttons carry their own white background, no header bar needed */}
      <div className="fixed top-0 left-0 right-0 z-40 px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => nav(backTo)}
            className="w-9 h-9 rounded-2xl bg-white/90 backdrop-blur border border-white/60 shadow-sm flex items-center justify-center hover:bg-white transition-all active:scale-90"
            aria-label="Назад"
          >
            <ChevronLeft className="w-5 h-5 text-[color:var(--ink)]" />
          </button>
          <button
            onClick={() => setReportOpen(true)}
            className="w-9 h-9 rounded-2xl bg-white/90 backdrop-blur border border-white/60 shadow-sm flex items-center justify-center hover:bg-white transition-all active:scale-90"
            aria-label="Пожаловаться"
            data-testid="public-report-button"
          >
            <Flag className="w-4 h-4 text-[color:var(--peach-deep)]" />
          </button>
        </div>
      </div>

      {/* Photo section — bigger */}
      <div className="relative w-full h-[70vh] flex-shrink-0 overflow-hidden shadow-xl shadow-black/5">
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.3}
          onDragEnd={handleSwipe}
          className="w-full h-full"
          style={{ touchAction: "pan-y" }}
        >
          {current ? (
            <div className="relative w-full h-full">
              <img
                src={current.data_url}
                alt=""
                className={`w-full h-full object-cover ${current.blurred ? "photo-blur" : ""}`}
                onClick={() => current.blurred ? null : setExpanded(true)}
                draggable={false}
              />
              {current.blurred && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <Camera className="w-10 h-10 text-white/50 mb-3" />
                  <div className="text-white font-[Onest] font-bold text-lg text-center px-6 drop-shadow-lg">
                    Загрузи ещё фото
                  </div>
                  <div className="text-white/70 text-sm mt-1 text-center px-6 drop-shadow">
                    Чтобы увидеть все фото, загрузи свои
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[color:var(--peach-bg)] to-[color:var(--peach)]/20" />
          )}
        </motion.div>

        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />

        {/* Photo dots */}
        {photos.length > 1 && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPhotoIdx(i)}
                className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                  i === photoIdx ? "w-8 bg-white shadow-sm" : "w-1.5 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}

        {/* Photo count badge */}
        <div className="absolute top-[4.5rem] right-4 z-10 text-[10px] px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md text-white/90 border border-white/20 inline-flex items-center gap-1">
          {photoIdx + 1}/{photos.length}
        </div>

        {/* Name and location — directly on the photo, no floating card */}
        <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
          <div className="font-[Onest] font-extrabold text-2xl text-white flex items-center gap-1.5 drop-shadow-sm">
            {profile.first_name}{profile.age != null ? `, ${profile.age}` : ""}
            {profile.is_premium && <PremiumBadge variant="full" />}
          </div>
          <div className="mt-1 text-xs text-white/85 flex items-center gap-1 drop-shadow-sm">
            <MapPin className="w-3 h-3" />
            <span>
              {profile.city}
              {profile.distance_km != null ? ` · ${Math.round(profile.distance_km)} км` : ""}
            </span>
          </div>
          {profile.username_visible && (
            <div className="mt-0.5 text-xs text-white/80 font-semibold drop-shadow-sm">
              @{profile.username_visible}
            </div>
          )}
        </div>
      </div>

      {/* Content below photo */}
      <div className="px-4 pt-4 pb-8 space-y-3">
        {/* Activity + Compatibility row */}
        <div className="flex gap-2">
          {activityText && (
            <div className="flex-1 rounded-2xl bg-white border border-[color:var(--border)] shadow-sm p-3">
              <div className="text-[9px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1 font-semibold flex items-center gap-1">
                <Clock className="w-3 h-3" /> Активность
              </div>
              <div className={`text-sm font-semibold flex items-center gap-1.5 ${activityColor}`}>
                <span className={`w-2 h-2 rounded-full ${activityText === "В сети" ? "bg-green-500 animate-pulse" : "bg-[color:var(--ink-soft)]"}`} />
                {activityText}
              </div>
            </div>
          )}
          <div className="flex-1 rounded-2xl bg-white border border-[color:var(--border)] shadow-sm p-3">
            <div className="text-[9px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1 font-semibold flex items-center gap-1">
              <Zap className="w-3 h-3" /> Совместимость
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 rounded-full bg-[color:var(--peach-bg)] overflow-hidden">
                <div className={`h-full rounded-full bg-gradient-to-r ${compatColor} transition-all duration-700`} style={{ width: `${compatScore}%` }} />
              </div>
              <span className="text-xs font-bold text-[color:var(--ink)]">{compatScore}%</span>
            </div>
            <div className="text-[10px] text-[color:var(--ink-soft)] mt-0.5">{compatLabel}</div>
          </div>
        </div>

        {/* Bio */}
        {profile.bio ? (
          <div className="rounded-2xl bg-white border border-[color:var(--border)] shadow-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--peach-deep)] mb-2 font-semibold">О себе</div>
            <div className="text-sm leading-relaxed text-[color:var(--ink)] whitespace-pre-wrap">{profile.bio}</div>
          </div>
        ) : null}

        {/* Seeking chips */}
        {seekingList.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {seekingList.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[color:var(--peach)]/10 border border-[color:var(--peach)]/20 text-xs font-medium text-[color:var(--peach-deep)]">
                  <Icon className="w-3 h-3" /> {s.label}
                </div>
              );
            })}
          </div>
        )}

        {/* Details — compact chips with SVG icons */}
        {details.length > 0 && (
          <div className="rounded-2xl bg-white border border-[color:var(--border)] shadow-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--peach-deep)] mb-3 font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> О человеке
            </div>
            <div className="flex flex-wrap gap-1.5">
              {details.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.id} className="px-3 py-2 rounded-xl bg-[color:var(--peach-bg)] border border-[color:var(--border)] flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-[color:var(--peach-deep)] flex-shrink-0" />
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[color:var(--ink-soft)]">{item.label}</div>
                      <div className="text-sm font-semibold text-[color:var(--ink)]">{item.value}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Preferences */}
        {profile.preferences ? (
          <div className="rounded-2xl bg-white border border-[color:var(--border)] shadow-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--peach-deep)] mb-2 font-semibold">О чём мечтает</div>
            <div className="text-sm leading-relaxed text-[color:var(--ink)] whitespace-pre-wrap">{profile.preferences}</div>
          </div>
        ) : null}

        {/* Action buttons — hidden once you're already matched with this person */}
        {!matched && (
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => sendLike("like")}
              disabled={liking}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-[color:var(--peach)] to-[color:var(--peach-deep)] text-white font-semibold text-sm shadow-lg shadow-[color:var(--peach-deep)]/20 active:scale-[0.97] transition-all disabled:opacity-50"
            >
              <Heart className="w-4 h-4" fill="white" /> Лайк
            </button>
            <button
              onClick={() => sendLike("superlike")}
              disabled={liking}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-[color:var(--teal-superlike)] to-[color:var(--teal-superlike)] text-white font-semibold text-sm shadow-lg shadow-[#2A9D8F]/20 active:scale-[0.97] transition-all disabled:opacity-50"
            >
              <Star className="w-4 h-4" fill="white" /> Суперлайк
            </button>
          </div>
        )}

        {/* Report button */}
        <button
          data-testid="public-report-cta"
          onClick={() => setReportOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-200 bg-red-50/50 text-red-400 hover:bg-red-50 transition-colors text-xs font-medium mt-1"
        >
          <Flag className="w-3 h-3" />
          Пожаловаться
        </button>
      </div>

      {/* Report modal */}
      <AnimatePresence>
        {reportOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setReportOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.3 }}
              className="w-full max-w-md rounded-3xl bg-white border border-[color:var(--border)] p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-[Onest] font-extrabold text-xl">Пожаловаться</div>
                <button
                  onClick={() => setReportOpen(false)}
                  className="w-8 h-8 rounded-full bg-[color:var(--peach-bg)] flex items-center justify-center text-[color:var(--ink-soft)]"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-sm text-[color:var(--ink-soft)] mb-4">
                На {profile.first_name}? Выберите причину — мы рассмотрим и примем меры.
              </div>

              {/* Reason chips */}
              <div className="grid grid-cols-1 gap-2 mb-4 max-h-[40vh] overflow-y-auto no-scrollbar pr-1">
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setReportReason(r.id)}
                    data-testid={`report-reason-${r.id}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors ${
                      reportReason === r.id
                        ? "border-[color:var(--peach-deep)] bg-[color:var(--peach)]/10"
                        : "border-[color:var(--border)] bg-white hover:bg-[color:var(--peach-bg)]"
                    }`}
                  >
                    <span className="text-sm">{r.icon ? <r.icon className="w-5 h-5 text-[color:var(--peach-deep)]" /> : "📝"}</span>
                    <span className="text-sm font-medium flex-1">{r.label}</span>
                    {reportReason === r.id && <Check className="w-4 h-4 text-[color:var(--peach-deep)] flex-shrink-0" />}
                  </button>
                ))}
              </div>

              {/* Comment */}
              <textarea
                value={reportComment}
                onChange={(e) => setReportComment(e.target.value.slice(0, 500))}
                placeholder="Дополнительные детали (необязательно)"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] text-sm resize-none mb-3"
                rows={2}
              />
              <div className="text-[11px] text-[color:var(--ink-soft)] -mt-2 mb-3 text-right">{reportComment.length}/500</div>

              <button
                data-testid="report-submit"
                onClick={submitReport}
                disabled={!reportReason || reportSending}
                className="w-full py-3 rounded-full bg-gradient-to-r from-[color:var(--peach-deep)] to-[color:var(--peach)] text-white font-semibold text-sm disabled:opacity-50 active:scale-[0.98] transition-all shadow-md"
              >
                {reportSending ? "Отправляем…" : "Отправить жалобу"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo expand modal */}
      {expanded && (
        <PhotoExpandModal
          photos={photos}
          currentIndex={photoIdx}
          onClose={() => setExpanded(false)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}

      <MatchModal
        open={!!matchModal}
        matched={matchModal}
        myFirstName={user?.first_name}
        onSendMessage={openChat}
        onKeepSwiping={() => setMatchModal(null)}
      />
    </div>
  );
}
